import React, { useEffect, useRef, Suspense } from 'react';
import { Routes, Route, useLocation, Navigate } from 'react-router-dom';
import Header from './components/Header';
import Footer from './components/Footer';
import BottomNav from './components/BottomNav';
import Toast from './components/Toast';
import DesktopSidebar from './components/menu/DesktopSidebar';
import { PredictionFeedSkeleton } from './components/prediction-feed/PredictionFeedSkeleton';
import PredictionSkeleton from './components/prediction/PredictionSkeleton';
import ContentDetailSkeleton from './components/content/ContentDetailSkeleton';
import { trackPageViewWhenTitleSettles } from './utils/analytics';
import dataTracker from './utils/dataTracker';
import { landings } from './data/landingsConfig';
import { countryBonuses } from './data/countryBonusesConfig';
import { AFFILIATE_HOP_SLUGS } from './components/bookmakers/config/bookmakerSelectors';
import useTheme from './hooks/useTheme';

// Критичные компоненты загружаем сразу (Header, Footer, etc)
// Остальные загружаем лениво
const Home = React.lazy(() => import('./pages/Home'));
const FullPrediction = React.lazy(() => import('./pages/FullPrediction'));
const Accumulators = React.lazy(() => import('./pages/Accumulators'));
const Bonuses = React.lazy(() => import('./pages/Bonuses'));
const BonusesCountry = React.lazy(() => import('./pages/BonusesCountry'));
const BonusesDetail = React.lazy(() => import('./pages/BonusesDetail'));
const Guides = React.lazy(() => import('./pages/Guides'));
const GuideDetail = React.lazy(() => import('./pages/GuideDetail'));
const NotFound = React.lazy(() => import('./pages/NotFound'));
const PredictionsTags = React.lazy(() => import('./pages/PredictionsTags'));
const About = React.lazy(() => import('./pages/About'));
const PrivacyPolicy = React.lazy(() => import('./pages/PrivacyPolicy'));
const AdminPanel = React.lazy(() => import('./pages/AdminPanel'));
const MiniLanding = React.lazy(() => import('./pages/MiniLanding'));
const Results = React.lazy(() => import('./pages/Results'));
const PredictionsByDate = React.lazy(() => import('./pages/PredictionsByDate'));
const BestBettingSites = React.lazy(() => import('./pages/BestBettingSites'));
const MagnetHub = React.lazy(() => import('./pages/MagnetHub'));

const KNOWN_ROUTE_PREFIXES = [
  '/accumulators',
  '/best-betting-sites',
  '/bookie-bonuses',
  '/betting-guides',
  '/about',
  '/privacy-policy',
  '/djimbo',
  '/1xbet-predictions',
  '/melbet-predictions',
  '/betwinner-predictions',
  '/predictions',
  '/results',
  '/404',
  ...AFFILIATE_HOP_SLUGS.map((slug) => `/${slug}`),
];

const isPredictionDetailPath = (pathname) => {
  const path = pathname.replace(/\/$/, '') || '/';
  if (path === '/') return false;
  if (KNOWN_ROUTE_PREFIXES.some((prefix) => path === prefix || path.startsWith(`${prefix}/`))) {
    return false;
  }
  if (countryBonuses.some((entry) => {
    const route = entry.route.replace(/\/$/, '');
    return path === route || path.startsWith(`${route}/`);
  })) {
    return false;
  }
  if (landings.some((landing) => path === `/${landing.slug}`)) {
    return false;
  }
  return /^\/[^/]+$/.test(path);
};

const RouteSuspenseFallback = () => {
  const { pathname } = useLocation();

  if (pathname === '/' || pathname.startsWith('/predictions/')) {
    return (
      <div className="container-narrow page-y">
        <PredictionFeedSkeleton withLeagues />
      </div>
    );
  }

  if (isPredictionDetailPath(pathname)) {
    return (
      <div className="container-narrow page-y-detail">
        <PredictionSkeleton />
      </div>
    );
  }

  const detailMatch = pathname.match(/^\/(betting-guides|bookie-bonuses)\/[^/]+\/?$/);
  if (detailMatch) {
    return <ContentDetailSkeleton variant={detailMatch[1] === 'bookie-bonuses' ? 'bonus' : 'guide'} />;
  }

  // Generic page shape (header + cards) while the route chunk loads; pages show their own skeleton next
  return (
    <div className="container-narrow page-y animate-pulse">
      <div className="h-7 w-56 bg-gray-200 dark:bg-gray-700 rounded mb-2" />
      <div className="h-4 w-72 max-w-full bg-gray-200 dark:bg-gray-700 rounded mb-6" />
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-40 rounded-2xl bg-gray-200 dark:bg-gray-700" />
        ))}
      </div>
    </div>
  );
};

const AppRoutes = () => (
  <Suspense fallback={<RouteSuspenseFallback />}>
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/results" element={<Results />} />
      <Route path="/best-betting-sites" element={<BestBettingSites />} />
      {/* Hub content stays in the lazy chunk; unknown slugs redirect to /404 inside MagnetHub */}
      <Route path="/best-betting-sites/:hubSlug" element={<MagnetHub />} />
      <Route path="/predictions/:date" element={<PredictionsByDate />} />
      <Route path="/accumulators/" element={<Accumulators />} />
      <Route path="/bookie-bonuses/" element={<Bonuses />} />
      {countryBonuses.map((entry) => (
        <Route
          key={entry.id}
          path={entry.route}
          element={<BonusesCountry config={entry} />}
        />
      ))}
      <Route path="/bookie-bonuses/:slug" element={<BonusesDetail />} />
      <Route path="/betting-guides/" element={<Guides />} />
      <Route path="/betting-guides/:slug" element={<GuideDetail />} />
      <Route path="/about" element={<About />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/djimbo" element={<AdminPanel />} />
      <Route path="/1xbet-predictions" element={<PredictionsTags />} />
      <Route path="/melbet-predictions" element={<PredictionsTags />} />
      <Route path="/betwinner-predictions" element={<PredictionsTags />} />
      <Route path="/404" element={<NotFound />} />
      {landings.map((landing) => (
        <Route key={landing.slug} path={`/${landing.slug}`} element={<MiniLanding key={landing.slug} config={landing} />} />
      ))}
      {AFFILIATE_HOP_SLUGS.map((slug) => (
        <Route
          key={slug}
          path={`/${slug}`}
          element={<Navigate to="/bookie-bonuses/" replace />}
        />
      ))}
      <Route path="/:slug" element={<FullPrediction />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  </Suspense>
);

// #anchor click or Back from one: the browser does the in-page jump and it is the same page view
const isHashOnlyChange = (prev, next) =>
  Boolean(prev) &&
  prev.pathname === next.pathname &&
  prev.search === next.search &&
  prev.hash !== next.hash;

const App = () => {
  const location = useLocation();
  const { darkMode, toggleDarkMode } = useTheme();
  const isAdminPage = location.pathname === '/djimbo';
  const showSiteSidebar = !isAdminPage;
  const prevLocationRef = useRef(null);

  useEffect(() => {
    const prevLocation = prevLocationRef.current;
    prevLocationRef.current = location;
    if (isHashOnlyChange(prevLocation, location)) return undefined;

    window.scrollTo(0, 0);
    // Admin panel views stay out of analytics
    if (isAdminPage) return undefined;
    // Leaving early flushes the pending view with the old page's (still current) title
    const flushPageView = trackPageViewWhenTitleSettles(location.pathname);
    dataTracker.trackPageView(location.pathname);
    return flushPageView;
  }, [location, isAdminPage]);

  useEffect(() => {
    dataTracker.initialize();
  }, []);

  // Keep the footer below the fold while route chunks and page data load (CLS)
  const mainClassName = 'flex-grow min-h-screen';

  return (
    <div className="min-h-screen flex flex-col">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[100] focus:rounded-lg focus:bg-primary focus:px-4 focus:py-2 focus:text-white focus:shadow-lg"
      >
        Skip to main content
      </a>
      {!showSiteSidebar && (
        <Header darkMode={darkMode} onToggleTheme={toggleDarkMode} />
      )}

      {showSiteSidebar ? (
        <div className="flex-1 flex flex-col lg:flex-row lg:max-w-[1400px] lg:mx-auto lg:px-6 lg:pt-4 lg:gap-8 w-full">
          <DesktopSidebar darkMode={darkMode} onToggleTheme={toggleDarkMode} />
          <div className="min-w-0 flex-1 flex flex-col">
            <Header contained darkMode={darkMode} onToggleTheme={toggleDarkMode} />
            <main id="main-content" className={mainClassName}>
              <AppRoutes />
            </main>
            <Footer />
          </div>
        </div>
      ) : (
        <>
          <main id="main-content" className={mainClassName}>
            <AppRoutes />
          </main>
          {/* Only the admin panel renders here: no site footer under it */}
        </>
      )}

      <BottomNav />

      <Toast />
    </div>
  );
};

export default App; 